/**
 * DEV ONLY — "Simulate sends" on a group's Today row: fakes sent facts +
 * fake stats on the generator's own rows (SMSWiser is never touched) so the
 * verdict pass can be watched end to end. Preset = how the day went.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { FlaskConical } from 'lucide-react';

import { ConfirmDialog, Input } from '@/components/ui';

import type { SimPreset } from '../../../simulation/useSimulation';

export interface SimulateRequest {
  preset: SimPreset;
  drops: number;
  sentMinutesAgo: number;
}

/** The DTO's bounds — never send a request the backend will refuse. */
const MAX_DROPS = 200;
const MAX_MINUTES = 1440;

const SEG = (on: boolean) =>
  `rounded px-2 py-1 text-xs font-semibold ${on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`;

export const SimulateControl: FC<{
  groupName: string;
  unsent: number;
  onSimulate: (req: SimulateRequest) => void;
  busy?: boolean;
}> = ({ groupName, unsent, onSimulate, busy = false }) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<SimPreset>('terrible');
  const [drops, setDrops] = useState(Math.max(1, unsent));
  const [minutesAgo, setMinutesAgo] = useState(60);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setDrops(Math.max(1, Math.min(MAX_DROPS, unsent)));
          setOpen(true);
        }}
        disabled={unsent === 0}
        title={
          unsent === 0
            ? 'No drafts to fake on this day — Generate it (again) first'
            : 'DEV — fake the sends of this group (nothing goes to SMSWiser) so you can Evaluate and watch the verdict act'
        }
        className="inline-flex shrink-0 items-center gap-1 rounded border border-dashed border-input px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:opacity-40"
      >
        <FlaskConical className="h-3 w-3" /> simulate
      </button>
      <ConfirmDialog
        open={open}
        title={`Simulate sends for ${groupName}`}
        confirmLabel="Simulate"
        busy={busy}
        onConfirm={() => {
          onSimulate({
            preset,
            drops: Math.max(1, Math.min(drops, unsent, MAX_DROPS)),
            sentMinutesAgo: Math.min(minutesAgo, MAX_MINUTES),
          });
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
        description={
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-muted-foreground">
              Development tool. Marks drafts as sent on the generator&apos;s own
              rows with made-up stats — <b>nothing is sent on SMSWiser</b>. Then
              press <b>Evaluate verdicts</b> to watch the judge act on them.
            </p>
            <div className="flex items-center gap-2">
              <span className="w-28 text-xs text-muted-foreground">The day was</span>
              <span className="flex items-center gap-0.5 rounded-md border border-border/60 p-0.5">
                <button type="button" className={SEG(preset === 'healthy')} onClick={() => setPreset('healthy')}>
                  healthy
                </button>
                <button type="button" className={SEG(preset === 'terrible')} onClick={() => setPreset('terrible')}>
                  terrible
                </button>
              </span>
              <span className="text-[11px] text-muted-foreground">
                {preset === 'terrible' ? '≈ delivered 62% · bot clicks 35%' : '≈ delivered 95% · bot clicks 3%'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-28 text-xs text-muted-foreground">Drops to fake</span>
              <div className="w-20">
                <Input type="number" className="h-8 text-center" value={drops} onChange={(e) => setDrops(Math.max(1, Math.min(unsent, MAX_DROPS, Number(e.target.value) || 1)))} />
              </div>
              <span className="text-[11px] text-muted-foreground">of {unsent} unsent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-28 text-xs text-muted-foreground">Sent … ago</span>
              <div className="w-20">
                <Input type="number" className="h-8 text-center" value={minutesAgo} onChange={(e) => setMinutesAgo(Math.max(0, Math.min(MAX_MINUTES, Number(e.target.value) || 0)))} />
              </div>
              <span className="text-[11px] text-muted-foreground">min — under the settle time reads &quot;pending&quot;</span>
            </div>
          </div>
        }
      />
    </>
  );
};
